import React, { useState, useEffect, useCallback } from 'react';
import { LLMAnalysisEnhancement, UserRequest, Account } from '../types';
import { Copy, ThumbsUp, ThumbsDown, AlertTriangle, Heart, Shield, ChevronDown, ChevronUp, Settings, Edit2, Calendar, AlertCircle } from 'lucide-react';
import { api } from '../services/api';
import { usePlatformAuth } from '../contexts/AuthContext';
import { CombinedSoapNoteEditor } from './CombinedSoapNoteEditor';

interface PrenatalVisitInitialNotesProps {
  llmAnalysisEnhancement: LLMAnalysisEnhancement | null;
  userRequest?: UserRequest | null;
  account?: Account | null;
}

export const PrenatalVisitInitialNotesComponent: React.FC<PrenatalVisitInitialNotesProps> = ({
  llmAnalysisEnhancement,
  userRequest,
  account: accountProp
}) => {
  const auth = usePlatformAuth();
  const [account, setAccount] = useState<Account | null>(accountProp || null);

  // Load account if not provided as prop
  useEffect(() => {
    if (accountProp) {
      setAccount(accountProp);
      return;
    }

    if (!auth.isAuthenticated) return;

    let cancelled = false;

    async function loadAccount() {
      try {
        const connectedId = import.meta.env.VITE_STRIPE_CONNECTED_ACCOUNT_ID;
        if (!connectedId) return;
        
        const idToken = await auth.getIdToken();
        if (!idToken) return;
        
        const acct = await api.getAccount(connectedId, idToken);
        if (!cancelled) {
          setAccount(acct);
        }
      } catch (err) {
        console.error('Failed to load account for placeholder replacement:', err);
      }
    }

    loadAccount();
    
    return () => {
      cancelled = true;
    };
  }, [auth.isAuthenticated, accountProp]);

  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    'soap': true,
    'patient-summary': true,
    'recommendations': true,
    'pregnancy-dating': true,
    'obstetric-history': true,
    'initial-labs': true,
    'genetic-screening': true,
    'safety-screening': true,
    'ultrasound': true,
    'counseling': true,
    'visit-extension': true,
    'key-concepts': true,
  });

  const [copiedStates, setCopiedStates] = useState<Record<string, boolean>>({});
  const [feedbackStates, setFeedbackStates] = useState<Record<string, boolean | null>>({});
  const [isSendModalOpen, setIsSendModalOpen] = useState(false);
  const [emailAddress, setEmailAddress] = useState<string>('');
  const [saveToPatient, setSaveToPatient] = useState<boolean>(true);
  const [sending, setSending] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [editedSummary, setEditedSummary] = useState<string>('');
  
  // Combined SOAP note editing state
  const [editedCombinedSoapNote, setEditedCombinedSoapNote] = useState<string | undefined>(
    llmAnalysisEnhancement?.editedCombinedSoapNote
  );
  const [isSavingSoapNote, setIsSavingSoapNote] = useState(false);

  // Helper function to replace placeholders in patient summary
  const replacePlaceholders = (text: string): string => {
    if (!text) return text;
    let result = text;
    const clinicianName = account?.settings?.['Communication.ClinicianName'] || '';
    
    if (clinicianName) {
      result = result.replace(/\[Clinician's Name and Credentials\]/gi, clinicianName);
      result = result.replace(/\[Clinician Name and Credentials\]/gi, clinicianName);
      result = result.replace(/\[Clinician's Name\]/gi, clinicianName);
      result = result.replace(/\[Clinician Name\]/gi, clinicianName);
      result = result.replace(/\[Clinician\]/gi, clinicianName);
    }
    
    if (userRequest?.patientName) {
      result = result.replace(/\[Patient Name\]/gi, userRequest.patientName);
      result = result.replace(/\[Patient's Name\]/gi, userRequest.patientName);
      result = result.replace(/\[Patient\]/gi, userRequest.patientName);
    }
    
    return result;
  };

  const toggleSection = (sectionName: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionName]: !prev[sectionName]
    }));
  };

  const copyToClipboard = async (text: string, buttonKey: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedStates(prev => ({ ...prev, [buttonKey]: true }));
      setTimeout(() => {
        setCopiedStates(prev => ({ ...prev, [buttonKey]: false }));
      }, 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const handleFeedback = (sectionName: string, isPositive: boolean) => {
    setFeedbackStates(prev => ({
      ...prev,
      [sectionName]: isPositive
    }));
  };

  // Handle combined SOAP note edit save
  const handleCombinedSoapNoteSave = useCallback(async (editedCombinedNote: string) => {
    if (!userRequest?.id) {
      console.warn('No user request ID available for saving SOAP note edit');
      setEditedCombinedSoapNote(editedCombinedNote);
      return;
    }

    setIsSavingSoapNote(true);
    try {
      // Call API to save the edited combined SOAP note
      await api.saveCombinedSoapNoteEdit(userRequest.id, editedCombinedNote);
      
      // Update local state with the saved note
      setEditedCombinedSoapNote(editedCombinedNote);
      
      console.log('Combined SOAP note edit saved:', { editedCombinedNote });
    } catch (error) {
      console.error('Failed to save SOAP note edit:', error);
      alert('Failed to save SOAP note changes. Please try again.');
    } finally {
      setIsSavingSoapNote(false);
    }
  }, [userRequest?.id]);

  const handleOpenSettings = () => {
    window.dispatchEvent(new CustomEvent('openSettings'));
  };

  const openSendModal = async () => {
    try {
      if (userRequest?.patientId) {
        const patient = await api.getPatient(userRequest.patientId);
        setEmailAddress(patient.email || '');
      }
      if (!editedSummary && initialPrenatalData?.patientSummary) {
        setEditedSummary(replacePlaceholders(initialPrenatalData.patientSummary));
      }
    } catch (e) {
      console.warn('Failed to load patient for send modal', e);
    } finally {
      setIsSendModalOpen(true);
    }
  };

  const handleCloseSendModal = () => {
    setIsSendModalOpen(false);
    setShowSuccess(false);
  };

  const submitSend = async (summary: string) => {
    if (!userRequest?.patientId) {
      alert('Please associate a patient with this request before sending.');
      return;
    }
    if (!summary?.trim()) {
      alert('No patient summary available to send.');
      return;
    }
    if (!emailAddress?.trim()) {
      alert('Please enter a valid email address.');
      return;
    }
    try {
      setSending(true);
      await api.sendPatientSummary(userRequest.patientId, {
        requestId: userRequest.id,
        channel: 'email',
        destination: emailAddress,
        saveToPatient,
        summary
      });
      setShowSuccess(true);
      setTimeout(() => {
        setIsSendModalOpen(false);
        setShowSuccess(false);
      }, 500);
    } catch (e: any) {
      alert(`Failed to send: ${e?.message || 'Unknown error'}`);
    } finally {
      setSending(false);
    }
  };

  const formatkeyConcepts = (concepts: any[]): string => {
    let text = 'KEY CONCEPTS\n\n';
    concepts.forEach((concept, index) => {
      text += `${index + 1}. ${concept.label}\n`;
      text += `   Code: ${concept.code} (${concept.codeSystem})\n`;
      text += `   Justification: ${concept.justification}\n\n`;
    });
    return text;
  };

  const formatRecommendations = (recommendations: any[]): string => {
    let text = 'RECOMMENDATIONS\n\n';
    recommendations.forEach((rec, index) => {
      text += `${index + 1}. ${rec.action}\n`;
      text += `   Category: ${rec.category}\n`;
      text += `   Confidence: ${rec.confidence}\n\n`;
    });
    return text;
  };

  const renderSectionActions = (sectionName: string, content: string) => {
    const copyButtonKey = `${sectionName}-copy`;
    const isCopied = copiedStates[copyButtonKey];
    const feedback = feedbackStates[sectionName];

    return (
      <div className="flex items-center space-x-1">
        <button
          className="inline-flex items-center px-2 py-1 text-xs font-medium rounded border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          onClick={() => copyToClipboard(content, copyButtonKey)}
          title="Copy to clipboard"
        >
          <Copy className="w-3 h-3 mr-1" />
          {isCopied ? 'Copied!' : 'Copy'}
        </button>
        <button
          className={`inline-flex items-center p-1 rounded transition-colors duration-200 focus:outline-none focus:ring-1 focus:ring-gray-300 ${
            feedback === true 
              ? 'text-green-600 bg-green-50' 
              : 'text-gray-500 hover:text-green-600 hover:bg-green-50'
          }`}
          onClick={() => handleFeedback(sectionName, true)}
          title="Good"
        >
          <ThumbsUp className="w-3 h-3" />
        </button>
        <button
          className={`inline-flex items-center p-1 rounded transition-colors duration-200 focus:outline-none focus:ring-1 focus:ring-gray-300 ${
            feedback === false 
              ? 'text-red-600 bg-red-50' 
              : 'text-gray-500 hover:text-red-600 hover:bg-red-50'
          }`}
          onClick={() => handleFeedback(sectionName, false)}
          title="Needs improvement"
        >
          <ThumbsDown className="w-3 h-3" />
        </button>
      </div>
    );
  };

  const renderCodedConcept = (concept: any, index: number) => (
    <div key={index} className="card bg-base-100 shadow-sm border border-base-300">
      <div className="card-body p-4">
        <div className="flex items-center gap-2 mb-2">
          <span className="badge badge-primary badge-sm">{concept.codeSystem}</span>
          <span className="badge badge-outline badge-sm font-mono">{concept.code}</span>
        </div>
        <h4 className="card-title text-base">{concept.label}</h4>
        <p className="text-sm text-base-content/80">{concept.justification}</p>
      </div>
    </div>
  );

  const renderRecommendation = (recommendation: any, index: number) => (
    <div key={index} className="card bg-base-100 shadow-sm border border-base-300">
      <div className="card-body p-4">
        <div className="flex items-center gap-2 mb-2">
          <span className="badge badge-secondary badge-sm">{recommendation.category}</span>
          <span className="badge badge-outline badge-sm">{recommendation.confidence}</span>
        </div>
        <h4 className="card-title text-base">{recommendation.action}</h4>
      </div>
    </div>
  );

  const renderLabResult = (label: string, lab: any) => {
    if (!lab || (!lab.value && !lab.result && !lab.resultSummary && !lab.status)) return null;
    return (
      <div className="card bg-base-100 shadow-sm border border-base-300">
        <div className="card-body p-3">
          <div className="text-xs text-base-content/80 uppercase tracking-wide font-semibold mb-1">{label}</div>
          {lab.value && <div className="text-base-content font-medium">{lab.value}</div>}
          {lab.result && <div className="text-base-content">{lab.result}</div>}
          {lab.resultSummary && <div className="text-base-content">{lab.resultSummary}</div>}
          {lab.status && <span className="badge badge-sm badge-info mt-1">{lab.status}</span>}
        </div>
      </div>
    );
  };

  if (!llmAnalysisEnhancement?.initialPrenatalOutput) {
    return (
      <div className="text-center py-8">
        <div className="mx-auto flex items-center justify-center h-12 w-12 bg-orange-100 rounded-full mb-4">
          <AlertTriangle className="h-6 w-6 text-orange-600" />
        </div>
        <div className="max-w-md mx-auto">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Initial Prenatal Visit Analysis Available</h3>
          <p className="text-gray-600 mb-4">
            No initial prenatal visit analysis was generated for this request.
          </p>
        </div>
      </div>
    );
  }

  const initialPrenatalData = llmAnalysisEnhancement.initialPrenatalOutput;
  const extraction = initialPrenatalData.structuredExtraction;
  const extension = initialPrenatalData.prenatalVisitInitialExtension;

  const patientSummaryText = initialPrenatalData?.patientSummary || '';
  
  useEffect(() => {
    if (!patientSummaryText) return;
    const replaced = replacePlaceholders(patientSummaryText);
    if (replaced !== patientSummaryText || !editedSummary) {
      setEditedSummary(replaced);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account?.settings?.['Communication.ClinicianName'], userRequest?.patientName, patientSummaryText]);

  return (
    <>
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-base-content">Initial Prenatal Visit</h3>
          <p className="text-sm text-base-content/60">New OB Intake</p>
        </div>
      </div>

      <div className="space-y-4">
        {/* SOAP Note - using CombinedSoapNoteEditor */}
        {initialPrenatalData.structuredSoap && (
          <CombinedSoapNoteEditor
            originalNote={initialPrenatalData.structuredSoap}
            editedCombinedNote={editedCombinedSoapNote}
            isExpanded={expandedSections['soap']}
            onToggleExpand={() => toggleSection('soap')}
            onSave={handleCombinedSoapNoteSave}
            isSaving={isSavingSoapNote}
            onCopy={(text) => copyToClipboard(text, 'soap-copy')}
            isCopied={copiedStates['soap-copy']}
            onFeedback={(isPositive) => handleFeedback('soap', isPositive)}
            feedbackState={feedbackStates['soap']}
          />
        )}

        {/* Patient Summary */}
        {initialPrenatalData.patientSummary && (
          <div className="bg-base-100 border border-base-300 rounded-lg">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 border-b border-base-300">
              <div className="flex items-center gap-3">
                <button onClick={() => toggleSection('patient-summary')} className="p-1 hover:bg-base-200 rounded transition-colors">
                  {expandedSections['patient-summary'] ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </button>
                <span className="text-lg font-medium">Patient Summary</span>
              </div>
              <div className="flex items-center gap-2 flex-wrap self-end sm:self-auto">
                <button onClick={handleOpenSettings} className="btn btn-ghost btn-sm btn-circle" title="Open settings">
                  <Settings className="w-4 h-4" />
                </button>
                <button className="btn btn-primary btn-sm" onClick={openSendModal} title="Edit and send summary">
                  <Edit2 className="w-4 h-4 mr-1" />
                  Edit & Send
                </button>
                {renderSectionActions('patient-summary', initialPrenatalData.patientSummary)}
              </div>
            </div>
            {expandedSections['patient-summary'] && (
              <div className="p-4">
                <p className="text-gray-800 leading-relaxed whitespace-pre-line">
                  {editedSummary || replacePlaceholders(initialPrenatalData.patientSummary)}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Recommendations */}
        {initialPrenatalData.recommendations?.length > 0 && (
          <div className="bg-base-100 border border-base-300 rounded-lg">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 border-b border-base-300">
              <div className="flex items-center gap-3">
                <button onClick={() => toggleSection('recommendations')} className="p-1 hover:bg-base-200 rounded transition-colors">
                  {expandedSections['recommendations'] ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </button>
                <span className="text-lg font-medium">Recommendations</span>
              </div>
              <div className="self-end sm:self-auto">
                {renderSectionActions('recommendations', formatRecommendations(initialPrenatalData.recommendations))}
              </div>
            </div>
            {expandedSections['recommendations'] && (
              <div className="p-4">
                <div className="space-y-3">
                  {initialPrenatalData.recommendations.map((rec, idx) => renderRecommendation(rec, idx))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Pregnancy Dating and Overview */}
        {extraction?.pregnancyDatingAndOverview && (
          <div className="bg-base-100 border border-base-300 rounded-lg">
            <div className="flex items-center gap-3 p-4 border-b border-base-300">
              <button onClick={() => toggleSection('pregnancy-dating')} className="p-1 hover:bg-base-200 rounded transition-colors">
                {expandedSections['pregnancy-dating'] ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </button>
              <span className="text-lg font-medium">Pregnancy Dating & Overview</span>
            </div>
            {expandedSections['pregnancy-dating'] && (
              <div className="p-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {extraction.pregnancyDatingAndOverview.pregnancyConfirmation && (
                    <div className="card bg-base-100 shadow-sm border border-base-300">
                      <div className="card-body p-3">
                        <div className="text-xs text-base-content/80 uppercase tracking-wide font-semibold mb-1">Confirmation</div>
                        <div className="text-base-content font-medium">{extraction.pregnancyDatingAndOverview.pregnancyConfirmation}</div>
                      </div>
                    </div>
                  )}
                  {extraction.pregnancyDatingAndOverview.gestationalAgeDisplay && (
                    <div className="card bg-base-100 shadow-sm border border-base-300">
                      <div className="card-body p-3">
                        <div className="text-xs text-base-content/80 uppercase tracking-wide font-semibold mb-1">Gestational Age</div>
                        <div className="text-base-content font-medium text-lg">{extraction.pregnancyDatingAndOverview.gestationalAgeDisplay}</div>
                      </div>
                    </div>
                  )}
                  {extraction.pregnancyDatingAndOverview.estimatedDueDateIso && (
                    <div className="card bg-base-100 shadow-sm border border-base-300">
                      <div className="card-body p-3">
                        <div className="text-xs text-base-content/80 uppercase tracking-wide font-semibold mb-1">Estimated Due Date</div>
                        <div className="text-base-content font-medium">{new Date(extraction.pregnancyDatingAndOverview.estimatedDueDateIso).toLocaleDateString()}</div>
                      </div>
                    </div>
                  )}
                  {extraction.pregnancyDatingAndOverview.datingMethod && (
                    <div className="card bg-base-100 shadow-sm border border-base-300">
                      <div className="card-body p-3">
                        <div className="text-xs text-base-content/80 uppercase tracking-wide font-semibold mb-1">Dating Method</div>
                        <div className="text-base-content font-medium">{extraction.pregnancyDatingAndOverview.datingMethod}</div>
                      </div>
                    </div>
                  )}
                  {extraction.pregnancyDatingAndOverview.gravidaPara && (
                    <div className="card bg-base-100 shadow-sm border border-base-300">
                      <div className="card-body p-3">
                        <div className="text-xs text-base-content/80 uppercase tracking-wide font-semibold mb-1">Gravida/Para</div>
                        <div className="text-base-content font-medium">{extraction.pregnancyDatingAndOverview.gravidaPara}</div>
                      </div>
                    </div>
                  )}
                  {extraction.pregnancyDatingAndOverview.pregnancyRiskLevel && (
                    <div className="card bg-base-100 shadow-sm border border-base-300">
                      <div className="card-body p-3">
                        <div className="text-xs text-base-content/80 uppercase tracking-wide font-semibold mb-1">Risk Level</div>
                        <div className={`text-base-content font-medium ${extraction.pregnancyDatingAndOverview.pregnancyRiskLevel?.toLowerCase().includes('high') ? 'text-error' : ''}`}>
                          {extraction.pregnancyDatingAndOverview.pregnancyRiskLevel}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                {extraction.pregnancyDatingAndOverview.highRiskReasons && extraction.pregnancyDatingAndOverview.highRiskReasons.length > 0 && (
                  <div className="mt-4">
                    <div className="text-sm font-semibold text-base-content/70 mb-2">High Risk Reasons:</div>
                    <div className="flex flex-wrap gap-2">
                      {extraction.pregnancyDatingAndOverview.highRiskReasons.map((reason, idx) => (
                        <span key={idx} className="badge badge-error badge-sm">{reason}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Obstetric and Gynecologic History */}
        {extraction?.obstetricAndGynecologicHistoryIntake && (
          <div className="bg-base-100 border border-base-300 rounded-lg">
            <div className="flex items-center gap-3 p-4 border-b border-base-300">
              <button onClick={() => toggleSection('obstetric-history')} className="p-1 hover:bg-base-200 rounded transition-colors">
                {expandedSections['obstetric-history'] ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </button>
              <span className="text-lg font-medium">Obstetric & Gynecologic History</span>
            </div>
            {expandedSections['obstetric-history'] && (
              <div className="p-4 space-y-4">
                {extraction.obstetricAndGynecologicHistoryIntake.obstetricHistoryNarrative && (
                  <div className="card bg-base-100 shadow-sm border border-base-300">
                    <div className="card-body p-4">
                      <div className="text-sm font-semibold text-base-content/70 mb-2">Obstetric History</div>
                      <p className="text-gray-800">{extraction.obstetricAndGynecologicHistoryIntake.obstetricHistoryNarrative}</p>
                    </div>
                  </div>
                )}
                {extraction.obstetricAndGynecologicHistoryIntake.priorPregnancyOutcomes && extraction.obstetricAndGynecologicHistoryIntake.priorPregnancyOutcomes.length > 0 && (
                  <div>
                    <div className="text-sm font-semibold text-base-content/70 mb-2">Prior Pregnancy Outcomes:</div>
                    <div className="space-y-2">
                      {extraction.obstetricAndGynecologicHistoryIntake.priorPregnancyOutcomes.map((outcome, idx) => (
                        <div key={idx} className="card bg-base-100 shadow-sm border border-base-300">
                          <div className="card-body p-3">
                            <div className="flex flex-wrap gap-2">
                              {outcome.outcome && <span className="badge badge-neutral badge-sm">{outcome.outcome}</span>}
                              {outcome.year && <span className="badge badge-outline badge-sm">{outcome.year}</span>}
                              {outcome.deliveryMode && <span className="badge badge-info badge-sm">{outcome.deliveryMode}</span>}
                              {outcome.gestationalAgeAtOutcomeWeeks && <span className="badge badge-outline badge-sm">{outcome.gestationalAgeAtOutcomeWeeks} weeks</span>}
                            </div>
                            {outcome.complications && <p className="text-sm text-error mt-2">{outcome.complications}</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {extraction.obstetricAndGynecologicHistoryIntake.priorComplications && extraction.obstetricAndGynecologicHistoryIntake.priorComplications.length > 0 && (
                  <div>
                    <div className="text-sm font-semibold text-base-content/70 mb-2">Prior Complications:</div>
                    <div className="flex flex-wrap gap-2">
                      {extraction.obstetricAndGynecologicHistoryIntake.priorComplications.map((c, idx) => (
                        <span key={idx} className="badge badge-warning badge-sm">{c}</span>
                      ))}
                    </div>
                  </div>
                )}
                {extraction.obstetricAndGynecologicHistoryIntake.gynecologicHistory && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {extraction.obstetricAndGynecologicHistoryIntake.gynecologicHistory.menstrualHistorySummary && (
                      <div className="card bg-base-100 shadow-sm border border-base-300">
                        <div className="card-body p-3">
                          <div className="text-xs text-base-content/80 uppercase tracking-wide font-semibold mb-1">Menstrual History</div>
                          <div className="text-base-content">{extraction.obstetricAndGynecologicHistoryIntake.gynecologicHistory.menstrualHistorySummary}</div>
                        </div>
                      </div>
                    )}
                    {extraction.obstetricAndGynecologicHistoryIntake.gynecologicHistory.priorPapHistorySummary && (
                      <div className="card bg-base-100 shadow-sm border border-base-300">
                        <div className="card-body p-3">
                          <div className="text-xs text-base-content/80 uppercase tracking-wide font-semibold mb-1">Prior Pap History</div>
                          <div className="text-base-content">{extraction.obstetricAndGynecologicHistoryIntake.gynecologicHistory.priorPapHistorySummary}</div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Ultrasound and Fetal assessment */}
        {extraction?.ultrasoundAndFetalAssessment?.ultrasoundPerformedFlag && (
          <div className="bg-base-100 border border-base-300 rounded-lg">
            <div className="flex items-center gap-3 p-4 border-b border-base-300">
              <button onClick={() => toggleSection('ultrasound')} className="p-1 hover:bg-base-200 rounded transition-colors">
                {expandedSections['ultrasound'] ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </button>
              <span className="text-lg font-medium">Ultrasound & Fetal assessment</span>
            </div>
            {expandedSections['ultrasound'] && (
              <div className="p-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {extraction.ultrasoundAndFetalAssessment.ultrasoundType && (
                    <div className="card bg-base-100 shadow-sm border border-base-300">
                      <div className="card-body p-3">
                        <div className="text-xs text-base-content/80 uppercase tracking-wide font-semibold mb-1">Ultrasound Type</div>
                        <div className="text-base-content font-medium">{extraction.ultrasoundAndFetalAssessment.ultrasoundType}</div>
                      </div>
                    </div>
                  )}
                  {extraction.ultrasoundAndFetalAssessment.pregnancyLocation && (
                    <div className="card bg-base-100 shadow-sm border border-base-300">
                      <div className="card-body p-3">
                        <div className="text-xs text-base-content/80 uppercase tracking-wide font-semibold mb-1">Pregnancy Location</div>
                        <div className="text-base-content font-medium">{extraction.ultrasoundAndFetalAssessment.pregnancyLocation}</div>
                      </div>
                    </div>
                  )}
                  {extraction.ultrasoundAndFetalAssessment.fetalHeartRateBpm && (
                    <div className="card bg-base-100 shadow-sm border border-base-300">
                      <div className="card-body p-3">
                        <div className="flex items-center gap-2 mb-1">
                          <Heart className="w-4 h-4 text-error" />
                          <div className="text-xs text-base-content/80 uppercase tracking-wide font-semibold">Fetal Heart Rate</div>
                        </div>
                        <div className="text-base-content font-medium">{extraction.ultrasoundAndFetalAssessment.fetalHeartRateBpm} bpm</div>
                      </div>
                    </div>
                  )}
                  {extraction.ultrasoundAndFetalAssessment.crownRumpLengthMm && (
                    <div className="card bg-base-100 shadow-sm border border-base-300">
                      <div className="card-body p-3">
                        <div className="text-xs text-base-content/80 uppercase tracking-wide font-semibold mb-1">Crown-Rump Length</div>
                        <div className="text-base-content font-medium">{extraction.ultrasoundAndFetalAssessment.crownRumpLengthMm} mm</div>
                      </div>
                    </div>
                  )}
                  {extraction.ultrasoundAndFetalAssessment.datingConclusion && (
                    <div className="card bg-base-100 shadow-sm border border-base-300 sm:col-span-2">
                      <div className="card-body p-3">
                        <div className="text-xs text-base-content/80 uppercase tracking-wide font-semibold mb-1">Dating Conclusion</div>
                        <div className="text-base-content font-medium">{extraction.ultrasoundAndFetalAssessment.datingConclusion}</div>
                      </div>
                    </div>
                  )}
                </div>
                {extraction.ultrasoundAndFetalAssessment.otherUltrasoundFindings && (
                  <div className="mt-3">
                    <div className="text-sm font-semibold text-base-content/70 mb-1">Other Findings:</div>
                    <p className="text-gray-800">{extraction.ultrasoundAndFetalAssessment.otherUltrasoundFindings}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Initial Labs */}
        {extraction?.initialLabsAndPrenatalScreeningThisPregnancy && (
          <div className="bg-base-100 border border-base-300 rounded-lg">
            <div className="flex items-center gap-3 p-4 border-b border-base-300">
              <button onClick={() => toggleSection('initial-labs')} className="p-1 hover:bg-base-200 rounded transition-colors">
                {expandedSections['initial-labs'] ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </button>
              <span className="text-lg font-medium">Initial Labs & Prenatal Screening</span>
            </div>
            {expandedSections['initial-labs'] && (
              <div className="p-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {renderLabResult('Blood Type/Rh', extraction.initialLabsAndPrenatalScreeningThisPregnancy.bloodTypeRh)}
                  {renderLabResult('Antibody Screen', extraction.initialLabsAndPrenatalScreeningThisPregnancy.antibodyScreen)}
                  {renderLabResult('CBC', extraction.initialLabsAndPrenatalScreeningThisPregnancy.cbc)}
                  {renderLabResult('Urine Culture', extraction.initialLabsAndPrenatalScreeningThisPregnancy.urineCulture)}
                  {renderLabResult('HIV', extraction.initialLabsAndPrenatalScreeningThisPregnancy.hiv)}
                  {renderLabResult('Syphilis', extraction.initialLabsAndPrenatalScreeningThisPregnancy.syphilis)}
                  {renderLabResult('Hepatitis B', extraction.initialLabsAndPrenatalScreeningThisPregnancy.hepatitisB)}
                  {renderLabResult('Hepatitis C', extraction.initialLabsAndPrenatalScreeningThisPregnancy.hepatitisC)}
                  {renderLabResult('Rubella Immunity', extraction.initialLabsAndPrenatalScreeningThisPregnancy.rubellaImmunity)}
                  {renderLabResult('Varicella Immunity', extraction.initialLabsAndPrenatalScreeningThisPregnancy.varicellaImmunity)}
                  {renderLabResult('Gonorrhea/Chlamydia', extraction.initialLabsAndPrenatalScreeningThisPregnancy.gonorrheaChlamydia)}
                </div>
                {extraction.initialLabsAndPrenatalScreeningThisPregnancy.otherLabsOrderedOrReviewed && extraction.initialLabsAndPrenatalScreeningThisPregnancy.otherLabsOrderedOrReviewed.length > 0 && (
                  <div className="mt-4">
                    <div className="text-sm font-semibold text-base-content/70 mb-2">Other Labs:</div>
                    <div className="flex flex-wrap gap-2">
                      {extraction.initialLabsAndPrenatalScreeningThisPregnancy.otherLabsOrderedOrReviewed.map((lab, idx) => (
                        <span key={idx} className="badge badge-info badge-sm">{lab}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Genetic Screening Counseling */}
        {extraction?.geneticScreeningCounseling && (extraction.geneticScreeningCounseling.aneuploidyScreeningDiscussion || extraction.geneticScreeningCounseling.carrierScreeningDiscussion) && (
          <div className="bg-base-100 border border-base-300 rounded-lg">
            <div className="flex items-center gap-3 p-4 border-b border-base-300">
              <button onClick={() => toggleSection('genetic-screening')} className="p-1 hover:bg-base-200 rounded transition-colors">
                {expandedSections['genetic-screening'] ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </button>
              <span className="text-lg font-medium">Genetic Screening Counseling</span>
            </div>
            {expandedSections['genetic-screening'] && (
              <div className="p-4 space-y-3">
                {extraction.geneticScreeningCounseling.aneuploidyScreeningDiscussion && (
                  <div className="card bg-base-100 shadow-sm border border-base-300">
                    <div className="card-body p-4">
                      <div className="text-sm font-semibold text-base-content/70 mb-2">Aneuploidy Screening Discussion</div>
                      <p className="text-gray-800">{extraction.geneticScreeningCounseling.aneuploidyScreeningDiscussion}</p>
                      {extraction.geneticScreeningCounseling.aneuploidyScreeningStatus && (
                        <span className="badge badge-info badge-sm mt-2">{extraction.geneticScreeningCounseling.aneuploidyScreeningStatus}</span>
                      )}
                    </div>
                  </div>
                )}
                {extraction.geneticScreeningCounseling.carrierScreeningDiscussion && (
                  <div className="card bg-base-100 shadow-sm border border-base-300">
                    <div className="card-body p-4">
                      <div className="text-sm font-semibold text-base-content/70 mb-2">Carrier Screening Discussion</div>
                      <p className="text-gray-800">{extraction.geneticScreeningCounseling.carrierScreeningDiscussion}</p>
                      {extraction.geneticScreeningCounseling.carrierScreeningStatus && (
                        <span className="badge badge-info badge-sm mt-2">{extraction.geneticScreeningCounseling.carrierScreeningStatus}</span>
                      )}
                    </div>
                  </div>
                )}
                {extraction.geneticScreeningCounseling.geneticsReferral && (
                  <div className="alert alert-info">
                    <span className="font-medium">Genetics Referral:</span> {extraction.geneticScreeningCounseling.geneticsReferral}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Social, Behavioral, and Safety Screening */}
        {extraction?.socialBehavioralAndSafetyScreening && (
          <div className="bg-base-100 border border-base-300 rounded-lg">
            <div className="flex items-center gap-3 p-4 border-b border-base-300">
              <button onClick={() => toggleSection('safety-screening')} className="p-1 hover:bg-base-200 rounded transition-colors">
                {expandedSections['safety-screening'] ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </button>
              <span className="text-lg font-medium">Social & Safety Screening</span>
            </div>
            {expandedSections['safety-screening'] && (
              <div className="p-4 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {extraction.socialBehavioralAndSafetyScreening.tobaccoUseStatus && (
                    <div className="card bg-base-100 shadow-sm border border-base-300">
                      <div className="card-body p-3">
                        <div className="text-xs text-base-content/80 uppercase tracking-wide font-semibold mb-1">Tobacco Use</div>
                        <div className="text-base-content">{extraction.socialBehavioralAndSafetyScreening.tobaccoUseStatus}</div>
                      </div>
                    </div>
                  )}
                  {extraction.socialBehavioralAndSafetyScreening.alcoholUseStatus && (
                    <div className="card bg-base-100 shadow-sm border border-base-300">
                      <div className="card-body p-3">
                        <div className="text-xs text-base-content/80 uppercase tracking-wide font-semibold mb-1">Alcohol Use</div>
                        <div className="text-base-content">{extraction.socialBehavioralAndSafetyScreening.alcoholUseStatus}</div>
                      </div>
                    </div>
                  )}
                  {extraction.socialBehavioralAndSafetyScreening.substanceUseStatus && (
                    <div className="card bg-base-100 shadow-sm border border-base-300">
                      <div className="card-body p-3">
                        <div className="text-xs text-base-content/80 uppercase tracking-wide font-semibold mb-1">Substance Use</div>
                        <div className="text-base-content">{extraction.socialBehavioralAndSafetyScreening.substanceUseStatus}</div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Depression/Anxiety Screening */}
                {extraction.socialBehavioralAndSafetyScreening.depressionAnxietyScreening?.screenedFlag && (
                  <div className="card bg-base-100 shadow-sm border border-base-300">
                    <div className="card-body p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertCircle className="w-4 h-4 text-primary" />
                        <span className="text-sm font-semibold text-base-content/70">Depression/Anxiety Screening</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {extraction.socialBehavioralAndSafetyScreening.depressionAnxietyScreening.toolName && (
                          <span className="badge badge-outline badge-sm">Tool: {extraction.socialBehavioralAndSafetyScreening.depressionAnxietyScreening.toolName}</span>
                        )}
                        {extraction.socialBehavioralAndSafetyScreening.depressionAnxietyScreening.score !== null && extraction.socialBehavioralAndSafetyScreening.depressionAnxietyScreening.score !== undefined && (
                          <span className="badge badge-outline badge-sm">Score: {extraction.socialBehavioralAndSafetyScreening.depressionAnxietyScreening.score}</span>
                        )}
                        {extraction.socialBehavioralAndSafetyScreening.depressionAnxietyScreening.resultInterpretation && (
                          <span className={`badge badge-sm ${extraction.socialBehavioralAndSafetyScreening.depressionAnxietyScreening.resultInterpretation.toLowerCase().includes('positive') ? 'badge-warning' : 'badge-success'}`}>
                            {extraction.socialBehavioralAndSafetyScreening.depressionAnxietyScreening.resultInterpretation}
                          </span>
                        )}
                      </div>
                      {extraction.socialBehavioralAndSafetyScreening.depressionAnxietyScreening.planIfPositive && (
                        <p className="text-sm mt-2">{extraction.socialBehavioralAndSafetyScreening.depressionAnxietyScreening.planIfPositive}</p>
                      )}
                    </div>
                  </div>
                )}

                {/* IPV Screening */}
                {extraction.socialBehavioralAndSafetyScreening.intimatePartnerViolenceScreening?.screenedFlag && (
                  <div className="card bg-base-100 shadow-sm border border-base-300">
                    <div className="card-body p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Shield className="w-4 h-4 text-primary" />
                        <span className="text-sm font-semibold text-base-content/70">IPV Screening</span>
                      </div>
                      {extraction.socialBehavioralAndSafetyScreening.intimatePartnerViolenceScreening.result && (
                        <span className={`badge badge-sm ${extraction.socialBehavioralAndSafetyScreening.intimatePartnerViolenceScreening.result.toLowerCase().includes('positive') ? 'badge-error' : 'badge-success'}`}>
                          {extraction.socialBehavioralAndSafetyScreening.intimatePartnerViolenceScreening.result}
                        </span>
                      )}
                      {extraction.socialBehavioralAndSafetyScreening.intimatePartnerViolenceScreening.safetyPlanOrResources && (
                        <p className="text-sm mt-2">{extraction.socialBehavioralAndSafetyScreening.intimatePartnerViolenceScreening.safetyPlanOrResources}</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Counseling and Education */}
        {extraction?.counselingAndEducationThisVisit && (
          <div className="bg-base-100 border border-base-300 rounded-lg">
            <div className="flex items-center gap-3 p-4 border-b border-base-300">
              <button onClick={() => toggleSection('counseling')} className="p-1 hover:bg-base-200 rounded transition-colors">
                {expandedSections['counseling'] ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </button>
              <span className="text-lg font-medium">Counseling & Education</span>
            </div>
            {expandedSections['counseling'] && (
              <div className="p-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {extraction.counselingAndEducationThisVisit.prenatalVitaminsAndFolicAcid && (
                    <div className="card bg-base-100 shadow-sm border border-base-300">
                      <div className="card-body p-3">
                        <div className="text-xs text-base-content/80 uppercase tracking-wide font-semibold mb-1">Prenatal Vitamins</div>
                        <div className="text-base-content">{extraction.counselingAndEducationThisVisit.prenatalVitaminsAndFolicAcid}</div>
                      </div>
                    </div>
                  )}
                  {extraction.counselingAndEducationThisVisit.nutritionAndWeightGainCounseling && (
                    <div className="card bg-base-100 shadow-sm border border-base-300">
                      <div className="card-body p-3">
                        <div className="text-xs text-base-content/80 uppercase tracking-wide font-semibold mb-1">Nutrition & Weight</div>
                        <div className="text-base-content">{extraction.counselingAndEducationThisVisit.nutritionAndWeightGainCounseling}</div>
                      </div>
                    </div>
                  )}
                  {extraction.counselingAndEducationThisVisit.exerciseCounseling && (
                    <div className="card bg-base-100 shadow-sm border border-base-300">
                      <div className="card-body p-3">
                        <div className="text-xs text-base-content/80 uppercase tracking-wide font-semibold mb-1">Exercise</div>
                        <div className="text-base-content">{extraction.counselingAndEducationThisVisit.exerciseCounseling}</div>
                      </div>
                    </div>
                  )}
                </div>
                {extraction.counselingAndEducationThisVisit.warningSignsReviewed && extraction.counselingAndEducationThisVisit.warningSignsReviewed.length > 0 && (
                  <div className="mt-4">
                    <div className="text-sm font-semibold text-base-content/70 mb-2">Warning Signs Reviewed:</div>
                    <div className="flex flex-wrap gap-2">
                      {extraction.counselingAndEducationThisVisit.warningSignsReviewed.map((sign, idx) => (
                        <span key={idx} className="badge badge-warning badge-sm">{sign}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Visit Extension / Orders */}
        {extension && (
          <div className="bg-base-100 border border-base-300 rounded-lg">
            <div className="flex items-center gap-3 p-4 border-b border-base-300">
              <button onClick={() => toggleSection('visit-extension')} className="p-1 hover:bg-base-200 rounded transition-colors">
                {expandedSections['visit-extension'] ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </button>
              <span className="text-lg font-medium">Visit Details & Orders</span>
            </div>
            {expandedSections['visit-extension'] && (
              <div className="p-4 space-y-4">
                <div className="flex flex-wrap gap-2">
                  {extension.trimester && <span className="badge badge-primary badge-sm">Trimester: {extension.trimester}</span>}
                  {extension.visitType && <span className="badge badge-outline badge-sm">Visit Type: {extension.visitType}</span>}
                </div>
                
                {extension.initialIntakeSectionsCompleted && extension.initialIntakeSectionsCompleted.length > 0 && (
                  <div>
                    <div className="text-sm font-semibold text-base-content/70 mb-2">Intake Sections Completed:</div>
                    <div className="flex flex-wrap gap-2">
                      {extension.initialIntakeSectionsCompleted.map((section, idx) => (
                        <span key={idx} className="badge badge-success badge-sm">{section}</span>
                      ))}
                    </div>
                  </div>
                )}

                {extension.ordersPlacedThisVisit && extension.ordersPlacedThisVisit.length > 0 && (
                  <div>
                    <div className="text-sm font-semibold text-base-content/70 mb-2">Orders Placed:</div>
                    <div className="flex flex-wrap gap-2">
                      {extension.ordersPlacedThisVisit.map((order, idx) => (
                        <span key={idx} className="badge badge-info badge-sm">{order}</span>
                      ))}
                    </div>
                  </div>
                )}

                {extension.referralsThisVisit && extension.referralsThisVisit.length > 0 && (
                  <div>
                    <div className="text-sm font-semibold text-base-content/70 mb-2">Referrals:</div>
                    <div className="flex flex-wrap gap-2">
                      {extension.referralsThisVisit.map((ref, idx) => (
                        <span key={idx} className="badge badge-secondary badge-sm">{ref}</span>
                      ))}
                    </div>
                  </div>
                )}

                {extension.pendingResultsOrFollowUps && extension.pendingResultsOrFollowUps.length > 0 && (
                  <div>
                    <div className="text-sm font-semibold text-base-content/70 mb-2">Pending Results/Follow-ups:</div>
                    <div className="flex flex-wrap gap-2">
                      {extension.pendingResultsOrFollowUps.map((item, idx) => (
                        <span key={idx} className="badge badge-warning badge-outline badge-sm">{item}</span>
                      ))}
                    </div>
                  </div>
                )}

                {extension.nextRecommendedVisitTiming && (
                  <div className="flex items-start gap-3 p-3 rounded-lg bg-info/10 border border-info/30">
                    <Calendar className="w-5 h-5 shrink-0 text-info" />
                    <div className="flex-1">
                      <div className="font-semibold text-sm mb-1 text-base-content">Next Visit</div>
                      <div className="text-sm text-base-content/80">{extension.nextRecommendedVisitTiming}</div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Key Concepts */}
        {initialPrenatalData.keyConcepts?.length > 0 && (
          <div className="bg-base-100 border border-base-300 rounded-lg">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 border-b border-base-300">
              <div className="flex items-center gap-3">
                <button onClick={() => toggleSection('key-concepts')} className="p-1 hover:bg-base-200 rounded transition-colors">
                  {expandedSections['key-concepts'] ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </button>
                <span className="text-lg font-medium">Key Concepts</span>
              </div>
              <div className="self-end sm:self-auto">
                {renderSectionActions('key-concepts', formatkeyConcepts(initialPrenatalData.keyConcepts))}
              </div>
            </div>
            {expandedSections['key-concepts'] && (
              <div className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {initialPrenatalData.keyConcepts.map((concept, idx) => renderCodedConcept(concept, idx))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Edit & Send Summary Modal */}
      {isSendModalOpen && (
        <dialog className="modal modal-open">
          <div className="modal-box max-w-3xl">
            <h3 className="font-bold text-lg mb-4">Edit & Send Summary to Patient</h3>
            <div className="space-y-4">
              <div className="form-control">
                <label className="label">
                  <span className="label-text font-medium">Patient Summary</span>
                </label>
                <textarea
                  value={editedSummary}
                  onChange={(e) => setEditedSummary(e.target.value)}
                  className="textarea textarea-bordered w-full min-h-[300px] font-mono text-sm"
                  placeholder="Edit patient summary..."
                  disabled={sending}
                />
              </div>
              <div className="form-control">
                <label className="label">
                  <span className="label-text font-medium">Email address</span>
                </label>
                <input
                  type="email"
                  placeholder="name@example.com"
                  className="input input-bordered w-full"
                  value={emailAddress}
                  onChange={(e) => setEmailAddress(e.target.value)}
                  disabled={sending}
                />
              </div>
              <div className="form-control">
                <label className="label cursor-pointer justify-start gap-2">
                  <input
                    type="checkbox"
                    className="checkbox checkbox-sm"
                    checked={saveToPatient}
                    onChange={(e) => setSaveToPatient(e.target.checked)}
                    disabled={sending}
                  />
                  <span className="label-text">Save email to patient profile</span>
                </label>
              </div>
            </div>
            {showSuccess && (
              <div className="text-center text-success text-sm mt-4 mb-2">
                Patient summary sent successfully!
              </div>
            )}
            <div className="modal-action mt-6">
              <button className="btn btn-ghost" onClick={handleCloseSendModal} disabled={sending}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={() => submitSend(editedSummary)}
                disabled={sending || !emailAddress.trim() || !editedSummary.trim()}
              >
                {sending ? 'Sending...' : 'Send Email'}
              </button>
            </div>
          </div>
          <form method="dialog" className="modal-backdrop" onClick={handleCloseSendModal}>
            <button>close</button>
          </form>
        </dialog>
      )}
    </div>
    </>
  );
};
